import { createAsyncThunk, createSelector, createSlice, PayloadAction, WritableDraft } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import axios from "axios";
import Session from "users/__session/session";
import { AccountType } from "users/account-types/account-type";
import TeamTemplateQuerySchema, { TeamTemplateQueryJSON } from "work/team-templates/_api/queries/_schema/team-template-query-schema";
import TeamTemplateQuery from "work/team-templates/_api/queries/team-template-query";
import TeamTemplateAPIService from "work/team-templates/_api/team-template-service";
import { TeamTemplateJSON } from "work/team-templates/_schema/team-template-schema";
import TeamTemplate from "work/team-templates/team-template";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";


export type TeamTemplatesStoreState = {
  byId: {
    entries: Record<string, TeamTemplateJSON>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  },
  byQuery: {
    entries: Record<string, PaginatedResponse<TeamTemplateJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  },
  interfaceQueries: {
    entries: Record<string, TeamTemplateQueryJSON>;
  }
};


const initialState: TeamTemplatesStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};


export const populateTeamTemplatesByQuery = createAsyncThunk(
  "team-templates/getTeamTemplatesByQuery",
  async ({ session, query }: { session: Session; query: TeamTemplateQuery }, thunkAPI) => {
    const apiService = new TeamTemplateAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getTeamTemplates(query, abortController);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const createTeamTemplate = createAsyncThunk(
  "team-templates/createTeamTemplate",
  async ({ session, entityId, team, teamName, creatorType }: { session: Session; entityId: Guid, team: VendorTeam, teamName: VendorTeamName, creatorType: AccountType }, thunkAPI) => {
    const apiService = new TeamTemplateAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.createTeamTemplate(entityId, team, teamName, creatorType);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const updateTeamTemplate = createAsyncThunk(
  "team-templates/updateTeamTemplate",
  async ({ session, originalTemplate, updatedTeam, updatedTeamName }: { session: Session; originalTemplate: TeamTemplate, updatedTeam: VendorTeam, updatedTeamName: VendorTeamName }, thunkAPI) => {
    const apiService = new TeamTemplateAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.updateTeamTemplate(originalTemplate, updatedTeam, updatedTeamName);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const deleteTeamTemplate = createAsyncThunk(
  "team-templates/deleteTeamTemplate",
  async ({ session, id }: { session: Session; id: Guid }, thunkAPI) => {
    const apiService = new TeamTemplateAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.deleteTeamTemplate(id);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const registerTeamTemplateToMarketplace = createAsyncThunk(
  "team-templates/registerTeamTemplateToMarketplace",
  async ({ session, templateId }: { session: Session; templateId: Guid }, thunkAPI) => {
    const apiService = new TeamTemplateAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.registerTeamTemplateToMarketplace(templateId);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);


const resetByQueryReducer = (state: WritableDraft<TeamTemplatesStoreState>) => {
  state.byQuery = initialState.byQuery;
};


const teamTemplatesSlice = createSlice({
  name: "team-templates",
  initialState,
  reducers: {
    updateTeamTemplatesInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: TeamTemplateQuery;
      }>
    ) => {
      const { interfaceName, query } = action.payload;

      state.interfaceQueries.entries[interfaceName] = TeamTemplateQuerySchema.serialize(query);
    },
    resetByQuery: resetByQueryReducer,
  },
  extraReducers: (builder) => {    
    builder.addCase(populateTeamTemplatesByQuery.pending, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }

      const queryKey = action.meta.arg.query.cacheKey;
      state.byQuery.loading[queryKey] = true;
      state.byQuery.error[queryKey] = null;
    });

    builder.addCase(populateTeamTemplatesByQuery.fulfilled, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }

      const queryKey = action.meta.arg.query.cacheKey;
      state.byQuery.loading[queryKey] = false;
      state.byQuery.entries[queryKey] = action.payload;

      for (const templateJson of action.payload.data) {
        state.byId.entries[templateJson.id!] = templateJson;
      }
    });

    builder.addCase(populateTeamTemplatesByQuery.rejected, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }

      const queryKey = action.meta.arg.query.cacheKey;
      state.byQuery.loading[queryKey] = false;
      state.byQuery.error[queryKey] = action.payload as string;
    });


    builder.addCase(createTeamTemplate.fulfilled, (state, action) => {
      const updated = action.payload;
      const id = updated?.id;
      if (!id) {
        return;
      }

      state.byId.entries[id] = updated;
      state.byId.loading[id] = false;
      state.byId.error[id] = null;

      resetByQueryReducer(state);
    });


    builder.addCase(updateTeamTemplate.pending, (state, action) => {
      const originalTemplate = action.meta.arg.originalTemplate;
      const id = originalTemplate.id?.value;
      if (!id) {
        return;
      }
        
      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });
    
    builder.addCase(updateTeamTemplate.fulfilled, (state, action) => {
      const updatedTemplate = action.payload;
      const id = updatedTemplate.id;
      if (!id) {
        return;
      }
      
      state.byId.entries[id] = updatedTemplate;
      state.byId.loading[id] = false;
      state.byId.error[id] = null;
    });
    
    builder.addCase(updateTeamTemplate.rejected, (state, action) => {
      const originalTemplate = action.meta.arg.originalTemplate;
      const id = originalTemplate.id?.value;
      if (!id) {
        return;
      }

      state.byId.entries[id] = originalTemplate.toJSON();
      state.byId.loading[id] = false;
      state.byId.error[id] = action.payload as string;
    });


    builder.addCase(deleteTeamTemplate.pending, (state, action) => {
      const id = action.meta.arg.id.value;

      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });

    builder.addCase(deleteTeamTemplate.fulfilled, (state, action) => {
      const id = action.meta.arg.id.value;

      delete state.byId.entries[id];
      state.byId.loading[id] = false;
      state.byId.error[id] = null;

      resetByQueryReducer(state);
    });

    builder.addCase(deleteTeamTemplate.rejected, (state, action) => {
      const id = action.meta.arg.id.value;

      state.byId.loading[id] = false;
      state.byId.error[id] = action.payload as string;
    });
  },
});


const selectTeamTemplateById = 
  createSelector(
    [
      (state: RootState) => state.teamTemplates.byId.entries,
      (_: RootState, id?: Guid) => id?.value,
    ],
    (entries, id) => {
      if (!id) return;
      const json = entries[id];
      if (!json) return;
      return TeamTemplate.fromJSON(json);
    }
  );
export const getTeamTemplateById = (id?: Guid) => (state: RootState) => 
  selectTeamTemplateById(state, id);
export const getIsLoadingTeamTemplateById = (id?: Guid) => (state: RootState) =>
  state.teamTemplates.byId.loading[id?.value ?? ""] ?? false;
export const getErrorLoadingTeamTemplateById = (id?: Guid) => (state: RootState) =>
  state.teamTemplates.byId.error[id?.value ?? ""] ?? false;


const selectTeamTemplatesByQuery =
  createSelector(
    [
      (state: RootState) => state.teamTemplates.byQuery.entries,
      (_: RootState, query?: TeamTemplateQuery | null) => query?.cacheKey,
    ],
    (entries, queryKey) => {
      if (!queryKey) return;
      const pagedJson = entries[queryKey];
      if (!pagedJson) return;
      const teamTemplates = TeamTemplate.fromJSONArray(pagedJson.data);
      return PaginatedResponse.from<TeamTemplate>(teamTemplates, pagedJson.headers);
    }
  );
export const getTeamTemplatesByQuery = (query?: TeamTemplateQuery | null) => (state: RootState) =>
  selectTeamTemplatesByQuery(state, query);
export const getIsLoadingTeamTemplatesByQuery = (query?: TeamTemplateQuery | null) => (state: RootState) => {
  if (!query) return false;
  return state.teamTemplates.byQuery.loading[query?.cacheKey ?? ""] ?? false;
};
export const getErrorLoadingTeamTemplatesByQuery = (query?: TeamTemplateQuery | null) => (state: RootState) => {
  if (!query) return false;
  return state.teamTemplates.byQuery.error[query?.cacheKey ?? ""] ?? false;
};


const selectTeamTemplatesQueryByInterface =
  createSelector(
    [
      (state: RootState) => state.teamTemplates.interfaceQueries.entries,
      (_: RootState, interfaceName: string) => interfaceName,
    ],
    (entries, interfaceName) => {
      const json = entries[interfaceName];
      if (!json) return;
      return TeamTemplateQuery.fromJSON(json);
    }
  );
export const getTeamTemplatesQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectTeamTemplatesQueryByInterface(state, interfaceName);


export const { updateTeamTemplatesInterfaceQuery } = teamTemplatesSlice.actions;

export default teamTemplatesSlice;